import React, { useState } from 'react';
import { Spinner } from '@nextui-org/react';

const FileUploadEnhanced = ({ uploadCb, email = '' }) => {
  const [state, setState] = useState({
    fileName: '',
    isError: false,
    isUploading: false,
    errorMessage: ''
  });

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      setState(prev => ({
        ...prev,
        isError: true,
        errorMessage: 'File size must be less than 2MB'
      }));
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setState(prev => ({
        ...prev,
        isError: true,
        errorMessage: 'Only PNG and JPG files are allowed'
      }));
      return;
    }

    setState(prev => ({
      ...prev,
      fileName: file.name,
      isError: false,
      errorMessage: ''
    }));

    try {
      await uploadCb(file, email).sub({
        start: () => setState(prev => ({ ...prev, isUploading: true, isError: false })),
        end: () => setState(prev => ({ ...prev, isUploading: false })),
        error: (err) => setState(prev => ({
          ...prev,
          isError: true,
          errorMessage: err.message || 'Upload failed. Please try again.'
        }))
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        isError: true,
        errorMessage: error.message || 'An unexpected error occurred'
      }));
    }
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <label
        htmlFor="uploadFile1"
        className={`
          relative bg-white text-black text-base rounded-lg w-full h-28 
          flex flex-col items-center justify-center cursor-pointer 
          border-2 border-dashed transition-all duration-200
          ${state.isError 
            ? 'border-red-500 hover:border-red-600' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${state.isUploading ? 'opacity-75 cursor-not-allowed' : ''}
        `}
      >
        {state.isUploading ? (
          <div className="flex flex-col items-center">
            <Spinner size="sm" color="primary" />
            <span className="mt-2 text-sm text-gray-600">Uploading...</span>
          </div>
        ) : (
          <>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className={`w-8 mb-2 ${state.isError ? 'fill-red-500' : 'fill-gray-600'}`} 
              viewBox="0 0 32 32"
            >
              <path d="M23.75 11.044a7.99 7.99 0 0 0-15.5-.009A8 8 0 0 0 9 27h3a1 1 0 0 0 0-2H9a6 6 0 0 1-.035-12 1.038 1.038 0 0 0 1.1-.854 5.991 5.991 0 0 1 11.862 0A1.08 1.08 0 0 0 23 13a6 6 0 0 1 0 12h-3a1 1 0 0 0 0 2h3a8 8 0 0 0 .75-15.956z" />
              <path d="M20.293 19.707a1 1 0 0 0 1.414-1.414l-5-5a1 1 0 0 0-1.414 0l-5 5a1 1 0 0 0 1.414 1.414L15 16.414V29a1 1 0 0 0 2 0V16.414z" />
            </svg>
            <span className="text-sm font-medium">
              {state.fileName || 'Upload file'}
            </span>
            <input 
              type="file" 
              onChange={handleFileChange} 
              id="uploadFile1" 
              className="hidden"
              accept=".png,.jpg,.jpeg"
              disabled={state.isUploading}
            />
            <p className="text-xs text-gray-400 mt-2">
              PNG, JPG image. Preferrably less than 2MB
            </p>
          </>
        )}
      </label>
      
      {state.isError && (
        <p className="mt-2 text-sm text-red-500 text-center">
          {state.errorMessage}
        </p>
      )}
    </div>
  );
};

export default FileUploadEnhanced;